// src/pages/Buyer/Dashboard.jsx
// ✅ FIXED:
// - invoiceAPI.getMyInvoices() → GET /buyer/invoices (backend route)
// - enquiryAPI.getUserEnquiries() response is plain array not wrapped
// - invoice._id → invoice.id (backend uses integer ids)
// - verificationAPI.getStatus() response shape handled safely

import React, { useState, useEffect } from 'react';
import {
  Container, Grid, Paper, Typography, Box, Button, Card, CardContent,
  Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, Alert, Avatar, Badge, IconButton, Tooltip, Tabs, Tab,
} from '@mui/material';
import {
  ShoppingCart, LocalShipping, AttachMoney, VerifiedUser, Add,
  ArrowForward, Warning, CheckCircle, Receipt, PictureAsPdf,
  Payment, Download, Visibility, AccountBalanceWallet, Anchor,
  LocationOn, Schedule,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { enquiryAPI, verificationAPI, userAPI } from '../../services/api';
import { formatDate, getStatusColor, formatCurrency } from '../../utils/helpers';
import toast from 'react-hot-toast';

const BuyerDashboard = () => {
  const { user } = useAuth();
  const navigate  = useNavigate();

  const [stats, setStats] = useState({
    totalEnquiries: 0, pendingEnquiries: 0, activeShipments: 0,
    pendingPayments: 0, totalInvoices: 0, pendingInvoices: 0,
    overdueInvoices: 0, totalAmountDue: 0,
  });
  const [enquiries,          setEnquiries]          = useState([]);
  const [invoices,           setInvoices]           = useState([]);
  const [shipments,          setShipments]          = useState([]);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [loading,            setLoading]            = useState(true);
  const [verificationLoading,setVerificationLoading]= useState(true);
  const [tabValue,           setTabValue]           = useState(0);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  useEffect(() => {
    if (user) {
      fetchDashboardData();
      fetchVerificationStatus();
      fetchShipments();
    }
  }, [user]);

  const fetchShipments = async () => {
    // Use the same enquiryAPI service (axios, auto-auth) — no raw fetch
    try {
      const res = await enquiryAPI.getUserEnquiries();
      const allEnqs = Array.isArray(res.data)
        ? res.data
        : (res.data?.enquiries || []);

      const SHIPPING_STATUSES = ['shipped', 'in_transit', 'confirmed', 'invoice_sent', 'completed', 'processing'];
      const shippingEnqs = allEnqs.filter(e => SHIPPING_STATUSES.includes(e.status));

      const mapped = shippingEnqs.map(e => ({
        id:                 `E${e.id}`,
        enquiry_id:         e.id,
        product:            e.product || e.productName || `Enquiry #${e.id}`,
        quantity:           e.quantity,
        status:             e.status,
        port_of_loading:    e.port_of_loading || null,
        port_of_discharge:  e.port_of_discharge || null,
        expected_arrival:   e.expected_arrival || e.eta || null,
        container_number:   e.container_number || null,
        bl_number:          e.bl_number || null,
      }));

      setShipments(mapped.slice(0, 5));
    } catch { /* silent — not critical */ }
  };

  const getArrivalDays = (arrival) => {
    if (!arrival) return null;
    const days = Math.ceil((new Date(arrival) - new Date()) / 86400000);
    if (days < 0)  return { label: `${Math.abs(days)}d overdue`, color: 'error' };
    if (days === 0) return { label: 'Arriving today!', color: 'success' };
    return { label: `${days} days`, color: days <= 7 ? 'warning' : 'info' };
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      // ✅ Run in parallel — don't let invoice failure block enquiries
      const [enquiriesRes, invoicesRes] = await Promise.allSettled([
        enquiryAPI.getUserEnquiries(),
        userAPI.getMyInvoices(),           // GET /user/invoices (inline route in __init__.py)
      ]);

      // ── Enquiries ────────────────────────────────────────────
      const userEnquiries = enquiriesRes.status === 'fulfilled'
        ? (Array.isArray(enquiriesRes.value.data)
            ? enquiriesRes.value.data
            : enquiriesRes.value.data?.enquiries || [])
        : [];

      // ── Invoices ─────────────────────────────────────────────
      const userInvoices = invoicesRes.status === 'fulfilled'
        ? (Array.isArray(invoicesRes.value.data)
            ? invoicesRes.value.data
            : invoicesRes.value.data?.invoices || [])
        : [];

      setEnquiries(userEnquiries.slice(0, 5));
      setInvoices(userInvoices.slice(0, 5));

      setStats({
        totalEnquiries:  userEnquiries.length,
        pendingEnquiries:userEnquiries.filter(e => e.status === 'pending').length,
        activeShipments: userEnquiries.filter(e =>
          ['confirmed', 'shipped', 'in_transit'].includes(e.status)).length,
        pendingPayments: userEnquiries.filter(e => e.status === 'confirmed').length,
        totalInvoices:   userInvoices.length,
        pendingInvoices: userInvoices.filter(i => i.status === 'pending').length,
        overdueInvoices: userInvoices.filter(i => i.status === 'overdue').length,
        totalAmountDue:  userInvoices
          .filter(i => ['pending', 'overdue'].includes(i.status))
          .reduce((sum, i) => sum + (i.totalAmount || i.total_amount || 0), 0),
      });
    } catch (error) {
      console.error('Dashboard data error:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchVerificationStatus = async () => {
    try {
      setVerificationLoading(true);
      const response = await verificationAPI.getStatus();
      // ✅ Handle both { user_verified: bool } and { verified: bool } shapes
      const data = response.data;
      setVerificationStatus({
        user_verified: data?.user_verified ?? data?.verified ?? false,
        ...data,
      });
    } catch {
      // Non-critical — user just hasn't submitted verification yet
      setVerificationStatus({ user_verified: false });
    } finally {
      setVerificationLoading(false);
    }
  };

  const handleDownloadInvoice = async (invoiceId, invoiceNumber) => {
    try {
      const response = await userAPI.downloadInvoice(invoiceId);
      const url  = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href  = url;
      link.setAttribute('download', `invoice-${invoiceNumber || invoiceId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Invoice downloaded');
    } catch {
      toast.error('Failed to download invoice');
    }
  };

  const statCards = [
    {
      title: 'Total Enquiries', value: stats.totalEnquiries,
      icon: <ShoppingCart sx={{ fontSize: 40 }} />, color: '#2e7d32',
      description: 'All enquiries made', action: () => navigate('/buyer/enquiries'),
    },
    {
      title: 'Pending Payments', value: formatCurrency(stats.totalAmountDue),
      icon: <AccountBalanceWallet sx={{ fontSize: 40 }} />, color: '#f44336',
      description: 'Total amount due', action: () => navigate('/buyer/invoices'),
    },
    {
      title: 'Active Shipments', value: stats.activeShipments,
      icon: <LocalShipping sx={{ fontSize: 40 }} />, color: '#2196f3',
      description: 'In transit', action: () => navigate('/buyer/shipments'),
    },
    {
      title: 'Pending Invoices', value: stats.pendingInvoices,
      icon: <Receipt sx={{ fontSize: 40 }} />, color: '#ff9800',
      description: 'Awaiting payment', action: () => navigate('/buyer/invoices'),
    },
  ];

  if (loading) {
    return (
      <Container sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading dashboard...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Welcome, {user?.company_name || user?.contact_person || user?.email}!
        </Typography>
        <Typography variant="body1" color="text.secondary">Your fruit trading dashboard</Typography>
      </Box>

      {/* Verification Alert */}
      {!verificationLoading && !verificationStatus?.user_verified && (
        <Alert severity="warning" sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" endIcon={<ArrowForward />}
              onClick={() => navigate('/buyer/verification')}>
              Complete Now
            </Button>
          }>
          <Typography variant="body1" fontWeight="medium">Verification Required</Typography>
          <Typography variant="body2">
            Please complete your business verification to make enquiries.
          </Typography>
        </Alert>
      )}

      {verificationStatus?.user_verified && (
        <Alert severity="success" sx={{ mb: 3 }} icon={<CheckCircle />}>
          <Typography variant="body1" fontWeight="medium">Account Verified ✓</Typography>
          <Typography variant="body2">Your business verification is complete.</Typography>
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {statCards.map((stat, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Card sx={{ cursor: 'pointer', transition: 'transform 0.2s',
                        '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 } }}
              onClick={stat.action}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="h5" sx={{ color: stat.color, fontWeight: 'bold' }}>
                      {stat.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">{stat.title}</Typography>
                    <Typography variant="caption" color="text.secondary">{stat.description}</Typography>
                  </Box>
                  <Box sx={{ color: stat.color }}>{stat.icon}</Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Quick Actions + Account Status + Payment Summary */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>Quick Actions</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button variant="contained" startIcon={<Add />} fullWidth size="large"
                onClick={() => navigate('/fruits')}>Browse Fruits</Button>
              {!verificationStatus?.user_verified && (
                <Button variant="outlined" color="warning" startIcon={<VerifiedUser />}
                  fullWidth size="large" onClick={() => navigate('/buyer/verification')}>
                  Complete Verification
                </Button>
              )}
              <Button variant="outlined" startIcon={<ShoppingCart />} fullWidth size="large"
                onClick={() => navigate('/buyer/enquiries')}>My Enquiries</Button>
              <Button variant="outlined" startIcon={<Receipt />} fullWidth size="large"
                onClick={() => navigate('/buyer/invoices')}>View Invoices</Button>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>Account Status</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {[
                { label: 'Email Verification', verified: true },
                { label: 'Business Verification', verified: verificationStatus?.user_verified,
                  onClick: !verificationStatus?.user_verified ? () => navigate('/buyer/verification') : undefined },
              ].map(({ label, verified, onClick }) => (
                <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body1">{label}</Typography>
                  {verificationLoading && label === 'Business Verification' ? (
                    <CircularProgress size={20} />
                  ) : (
                    <Chip label={verified ? 'Verified' : 'Pending'}
                      color={verified ? 'success' : 'warning'} size="small"
                      icon={verified ? <CheckCircle /> : <Warning />}
                      onClick={onClick} clickable={!!onClick} />
                  )}
                </Box>
              ))}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body1">Account Type</Typography>
                <Chip label="Buyer" color="primary" size="small" />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body1">Member Since</Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatDate(user?.created_at)}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>Payment Summary</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {[
                { label: 'Total Invoices', value: stats.totalInvoices },
                { label: 'Pending',        value: stats.pendingInvoices,  color: 'warning.main' },
                { label: 'Overdue',        value: stats.overdueInvoices,  color: 'error.main' },
              ].map(({ label, value, color }) => (
                <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body1">{label}</Typography>
                  <Typography variant="h6" color={color || 'text.primary'}>{value}</Typography>
                </Box>
              ))}
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body1">Amount Due</Typography>
                <Typography variant="h6" color="error.main">
                  {formatCurrency(stats.totalAmountDue)}
                </Typography>
              </Box>
              <Button variant="contained" startIcon={<Payment />} fullWidth sx={{ mt: 1 }}
                onClick={() => navigate('/buyer/invoices')}>
                Manage Payments
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Tabs: Recent Enquiries / Invoices */}
      <Paper sx={{ mb: 4 }}>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShoppingCart fontSize="small" /> Recent Enquiries
            </Box>
          } />
          <Tab label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Receipt fontSize="small" /> Recent Invoices
            </Box>
          } />
          <Tab label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocalShipping fontSize="small" /> Track Shipments
            </Box>
          } />
        </Tabs>

        {/* Enquiries Tab */}
        {tabValue === 0 && (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">Recent Enquiries</Typography>
              <Button variant="outlined" endIcon={<ArrowForward />}
                onClick={() => navigate('/buyer/enquiries')}>View All</Button>
            </Box>
            {enquiries.length === 0 ? (
              <Alert severity="info">
                No enquiries yet.{' '}
                <Button size="small" onClick={() => navigate('/fruits')}>Browse Fruits</Button>
              </Alert>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {['ID', 'Product', 'Qty', 'Date', 'Status', ''].map(h => (
                        <TableCell key={h}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {enquiries.map((e) => (
                      <TableRow key={e.id} hover>
                        <TableCell>#{e.id}</TableCell>
                        <TableCell>{e.product || e.productName || `Fruit #${e.fruit_id}`}</TableCell>
                        <TableCell>{e.quantity} containers</TableCell>
                        <TableCell>{formatDate(e.created_at)}</TableCell>
                        <TableCell>
                          <Chip label={e.status} color={getStatusColor(e.status)} size="small"
                            sx={{ textTransform: 'capitalize' }} />
                        </TableCell>
                        <TableCell>
                          <Button size="small" onClick={() => navigate(`/buyer/enquiries/${e.id}`)}>
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

        {/* Invoices Tab */}
        {tabValue === 1 && (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">Recent Invoices</Typography>
              <Button variant="outlined" endIcon={<ArrowForward />}
                onClick={() => navigate('/buyer/invoices')}>View All</Button>
            </Box>
            {invoices.length === 0 ? (
              <Alert severity="info">
                No invoices yet. Invoices appear once your enquiries are confirmed.
              </Alert>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {['Invoice #', 'Amount', 'Due Date', 'Status', 'Actions'].map(h => (
                        <TableCell key={h}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoices.map((inv) => {
                      // ✅ Support both _id (old) and id (new backend)
                      const invId = inv.id || inv._id;
                      return (
                        <TableRow key={invId} hover>
                          <TableCell>{inv.invoiceNumber || inv.invoice_number || `INV-${invId}`}</TableCell>
                          <TableCell>{formatCurrency(inv.totalAmount || inv.total_amount || 0)}</TableCell>
                          <TableCell>
                            <Typography variant="body2"
                              color={inv.status === 'overdue' ? 'error' : 'text.primary'}>
                              {formatDate(inv.dueDate || inv.due_date)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label={inv.status || 'pending'} size="small"
                              color={getStatusColor(inv.status)} sx={{ textTransform: 'capitalize' }} />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Tooltip title="View">
                                <IconButton size="small" color="primary"
                                  onClick={() => navigate(`/buyer/invoices/${invId}`)}>
                                  <Visibility fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Download PDF">
                                <IconButton size="small" color="secondary"
                                  onClick={() => handleDownloadInvoice(invId, inv.invoiceNumber)}>
                                  <PictureAsPdf fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              {['pending', 'overdue'].includes(inv.status) && (
                                <Tooltip title="Pay Now">
                                  <IconButton size="small" color="success"
                                    onClick={() => navigate(`/payment/${invId}`)}>
                                    <Payment fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

        {/* Shipments Tab */}
        {tabValue === 2 && (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">My Shipments</Typography>
              <Button variant="outlined" endIcon={<ArrowForward />}
                onClick={() => navigate('/buyer/shipments')}>View All</Button>
            </Box>
            {shipments.length === 0 ? (
              <Alert severity="info" icon={<LocalShipping />}>
                No shipments yet. Shipments appear once your order is confirmed and dispatched.
              </Alert>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {['#', 'Product', 'Container / BL', 'Route', 'ETA', 'Status', 'Action'].map(h => (
                        <TableCell key={h}><Typography variant="caption" fontWeight="bold">{h}</Typography></TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {shipments.map(s => {
                      const arr = getArrivalDays(s.expected_arrival);
                      return (
                        <TableRow key={s.id} hover>
                          <TableCell>#{s.id}</TableCell>
                          <TableCell>
                            <Typography variant="body2">{s.product || '—'}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{s.container_number || '—'}</Typography>
                            {s.bl_number && <Typography variant="caption" color="text.secondary">BL: {s.bl_number}</Typography>}
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">
                              {s.port_of_loading || '—'} → {s.port_of_discharge || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {arr ? (
                              <Chip label={arr.label} size="small" color={arr.color} variant="outlined" />
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                {formatDate(s.expected_arrival) || '—'}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={(s.status || 'pending').replace(/_/g, ' ')}
                              size="small"
                              color={s.status === 'delivered' ? 'success' : s.status === 'in_transit' ? 'info' : 'default'}
                              sx={{ textTransform: 'capitalize' }}
                            />
                          </TableCell>
                          <TableCell>
                            <Button size="small" variant="outlined" startIcon={<Anchor fontSize="small" />}
                              onClick={() => navigate('/buyer/shipments')}>
                              Track
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </Paper>

      {/* Alerts */}
      {stats.overdueInvoices > 0 && (
        <Alert severity="error" sx={{ mb: 3 }}
          action={<Button color="inherit" size="small" endIcon={<ArrowForward />}
            onClick={() => navigate('/buyer/invoices')}>View Overdue</Button>}>
          <Typography fontWeight="medium">
            {stats.overdueInvoices} overdue invoice(s) — please pay immediately.
          </Typography>
        </Alert>
      )}

      {stats.pendingInvoices > 0 && stats.overdueInvoices === 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}
          action={<Button color="inherit" size="small" endIcon={<ArrowForward />}
            onClick={() => navigate('/buyer/invoices')}>Pay Now</Button>}>
          <Typography fontWeight="medium">
            {stats.pendingInvoices} pending invoice(s) — total due: {formatCurrency(stats.totalAmountDue)}
          </Typography>
        </Alert>
      )}

      {!verificationStatus?.user_verified && (
        <Paper sx={{ p: 3, bgcolor: 'warning.light' }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Warning /> Next Steps Required
          </Typography>
          <Typography variant="body2" paragraph>
            Complete business verification and browse fruits to make your first enquiry.
          </Typography>
          <Button variant="contained" color="warning"
            onClick={() => navigate('/buyer/verification')} startIcon={<VerifiedUser />}>
            Start Verification
          </Button>
        </Paper>
      )}
    </Container>
  );
};

export default BuyerDashboard;